/**
 * Decrements in-flight download counter by one, clamped at zero.
 * @param {number} currentValue
 * @returns {number}
 */
const decrementInFlightDownloadsValue = currentValue =>
  Math.max(0, currentValue - 1);

/**
 * Decrements in-flight download counter by a validated amount, clamped at zero.
 * @param {number} currentValue
 * @param {number} count
 * @returns {number}
 */
const decrementInFlightDownloadsByValue = (currentValue, count) => {
  const safeCount = Number.isFinite(count)
    ? Math.max(0, Math.floor(count))
    : 1;
  return Math.max(0, currentValue - safeCount);
};

/**
 * Clears pipeline-scoped runtime buffers and resets transient flags.
 * @param {Object} params
 * @param {Array} params.parseQueue
 * @param {Object} params.pendingChunks
 * @param {Array<Object>} params.pipelineScopedSeriesStateStores
 * @returns {{inFlightDownloads:number,isParsing:boolean}}
 */
const resetPipelineRuntimeState = ({
  parseQueue,
  pendingChunks,
  pipelineScopedSeriesStateStores
}) => {
  parseQueue.length = 0;
  pipelineScopedSeriesStateStores.forEach(storeBySeriesId => {
    Object.keys(storeBySeriesId).forEach(seriesId => {
      delete storeBySeriesId[seriesId];
    });
  });
  Object.keys(pendingChunks).forEach(key => {
    delete pendingChunks[key];
  });
  return {
    inFlightDownloads: 0,
    isParsing: false
  };
};

/**
 * Counts pending chunk accumulators belonging to a series.
 * @param {Object} pendingChunks
 * @param {string|number} seriesId
 * @returns {number}
 */
const countPendingChunksForSeries = (pendingChunks, seriesId) =>
  Object.values(pendingChunks).reduce((count, item) => {
    return item && item.seriesId == seriesId ? count + 1 : count;
  }, 0);

/**
 * Counts parse-queued tasks belonging to a series.
 * @param {Array} parseQueue
 * @param {string|number} seriesId
 * @returns {number}
 */
const countParseQueueForSeries = (parseQueue, seriesId) =>
  parseQueue.reduce((count, task) => {
    return task && task.seriesId == seriesId ? count + 1 : count;
  }, 0);

/**
 * Removes runtime state entries for a specific series id.
 * @param {Object} context
 * @param {Object} context.downloadCounts
 * @param {Array<Object>} context.allSeriesStateStores
 * @param {string|number} seriesId
 * @returns {void}
 */
const clearSeriesRuntimeState = (
  { downloadCounts, allSeriesStateStores },
  seriesId
) => {
  delete downloadCounts[seriesId];
  allSeriesStateStores.forEach(storeBySeriesId => {
    delete storeBySeriesId[seriesId];
  });
};

/**
 * Converts supported payload formats into Uint8Array.
 * @param {Uint8Array|ArrayBuffer|ArrayBufferView|null} payload
 * @returns {Uint8Array|null}
 */
const toUint8Array = payload => {
  if (!payload) return null;
  if (payload instanceof Uint8Array) return payload;
  if (payload instanceof ArrayBuffer) return new Uint8Array(payload);
  if (ArrayBuffer.isView(payload)) {
    return new Uint8Array(
      payload.buffer,
      payload.byteOffset,
      payload.byteLength
    );
  }
  return null;
};

/**
 * Checks whether the DICOM API can parse raw bytes directly.
 * @param {Object} dicomApi
 * @returns {boolean}
 */
const supportsParseBytes = dicomApi =>
  typeof dicomApi?.parseBytes === "function" &&
  (typeof dicomApi?.supportsNativeParseBytes === "function"
    ? dicomApi.supportsNativeParseBytes()
    : true);

/**
 * Yields execution to allow the main thread to process pending work.
 * @returns {Promise<void>}
 */
const yieldToMainThread = () => new Promise(resolve => setTimeout(resolve, 0));

/**
 * Records a fatal websocket payload error, notifies UI, and resets transport.
 * @param {Object} params
 * @param {Object} params.downloadPerf
 * @param {Object} params.store
 * @param {Function} params.disconnect
 * @param {Error|string} [params.error]
 * @param {string} [params.userMessage]
 * @param {string} [params.perfStage]
 * @returns {void}
 */
const handleFatalSocketPayloadError = ({
  downloadPerf,
  store,
  disconnect,
  error,
  userMessage,
  perfStage = "ws_payload_fatal"
} = {}) => {
  downloadPerf.recordError({
    seriesId: null,
    stage: perfStage,
    message: error?.message || error || userMessage
  });
  if (userMessage) {
    store.dispatch("raiseSnackbar", {
      text: userMessage,
      color: "error",
      timeout: -1
    });
  }
  // A broken WS frame can desync in-flight counters; force a clean reset.
  disconnect();
};

export {
  clearSeriesRuntimeState,
  countParseQueueForSeries,
  countPendingChunksForSeries,
  decrementInFlightDownloadsByValue,
  decrementInFlightDownloadsValue,
  handleFatalSocketPayloadError,
  resetPipelineRuntimeState,
  supportsParseBytes,
  toUint8Array,
  yieldToMainThread
};
